import { useState } from 'react';
import { useGameStore } from '@/state/gameStore';
import {
  SKILL_WEB_NODES, getSkillWebNode, isNodeAvailable, nodeLevel,
  type SkillWebNode,
} from '@/domain/skillWeb';
import { canAfford, formatCost } from '@/domain/upgrade';
import type { ResourceId } from '@/domain/types';
import { useT, resourceLabel } from '@/i18n';
import './SkillWebPanel.css';

const RING_RADIUS: Record<SkillWebNode['ring'], number> = {
  core: 80,
  synergy: 160,
  capstone: 240,
};

const RING_COLORS: Record<SkillWebNode['ring'], string> = {
  core: '#36e8ff',
  synergy: '#4affb0',
  capstone: '#ff9a2b',
};

const SVG_SIZE = 560;
const CENTER = SVG_SIZE / 2;

function nodePos(node: SkillWebNode): { x: number; y: number } {
  const r = RING_RADIUS[node.ring];
  const rad = (node.angleDeg * Math.PI) / 180;
  // SVG y-axis is inverted (down = positive); flip so 90° = top
  return { x: CENTER + r * Math.cos(rad), y: CENTER - r * Math.sin(rad) };
}

export function SkillWebPanel() {
  const player = useGameStore((s) => s.player);
  const upgrade = useGameStore((s) => s.upgradeSkillWebNode);
  const respecSkillWeb = useGameStore((s) => s.respecSkillWeb);
  const t = useT();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!player) return null;
  const selected = selectedId ? getSkillWebNode(selectedId) : null;
  const respecCount = player.skillWebRespecCount ?? 0;
  const respecCost = respecCount === 0 ? 0 : 1000;
  const canAffordRespec = respecCount === 0 || (player.corruptedData ?? 0) >= 1000;
  const hasAnyInvestment = Object.values(player.skillWebNodes ?? {}).some((v) => (v ?? 0) > 0);

  const handleRespec = () => {
    const msg = respecCount === 0
      ? 'Alle Skill-Web-Investitionen werden zurückgesetzt und voll erstattet. Erster Respec ist kostenlos. Fortfahren?'
      : `Respec kostet 1000 Corrupted Data. Alle Skill-Web-Investitionen werden zurückgesetzt und voll erstattet. Fortfahren?`;
    if (confirm(msg)) {
      const ok = respecSkillWeb();
      if (!ok) alert('Respec fehlgeschlagen — nicht genug Corrupted Data.');
      else setSelectedId(null);
    }
  };

  return (
    <div className="skillweb-panel">
      <div className="skillweb-panel__header">
        <h2 className="skillweb-panel__title">{t.skillWeb.headline}</h2>
        <div className="skillweb-panel__sub">{t.skillWeb.sub}</div>
        {hasAnyInvestment && (
          <button
            type="button"
            className="skillweb-panel__respec-btn"
            onClick={handleRespec}
            disabled={!canAffordRespec}
            title={respecCost === 0 ? 'Erster Respec gratis' : `Kostet ${respecCost} ⌬`}
          >
            ↺ Respec {respecCost > 0 ? `(${respecCost} ⌬)` : '(gratis)'}
          </button>
        )}
      </div>

      <div className="skillweb-panel__body">
        <svg
          className="skillweb-svg"
          viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
          style={{ width: '100%', height: 'auto', maxWidth: 480 }}
        >
          {/* Concentric ring guides */}
          {(['core', 'synergy', 'capstone'] as const).map((ring) => (
            <circle
              key={ring}
              cx={CENTER} cy={CENTER}
              r={RING_RADIUS[ring]}
              fill="none"
              stroke={RING_COLORS[ring]}
              strokeOpacity={0.18}
              strokeDasharray="4 4"
            />
          ))}

          {/* Prerequisite lines */}
          {SKILL_WEB_NODES.map((node) =>
            node.prerequisites.map((req) => {
              const reqNode = getSkillWebNode(req.nodeId);
              if (!reqNode) return null;
              const a = nodePos(node);
              const b = nodePos(reqNode);
              const ok = nodeLevel(player, req.nodeId) >= req.minLevel;
              return (
                <line
                  key={`${node.id}-${req.nodeId}`}
                  x1={b.x} y1={b.y} x2={a.x} y2={a.y}
                  stroke={ok ? '#4affb0' : 'rgba(255,255,255,0.12)'}
                  strokeWidth={ok ? 1.5 : 1}
                  strokeOpacity={ok ? 0.5 : 0.3}
                />
              );
            }),
          )}

          {/* Nodes */}
          {SKILL_WEB_NODES.map((node) => {
            const pos = nodePos(node);
            const lv = nodeLevel(player, node.id);
            const available = isNodeAvailable(player, node);
            const maxed = lv >= node.maxLevel;
            const owned = lv >= 1;
            const ring = RING_COLORS[node.ring];
            const fill = !available ? '#222' : owned ? ring : 'rgba(20,30,50,0.85)';
            const stroke = maxed ? '#ffd84a' : available ? ring : '#444';
            const isSelected = selectedId === node.id;
            return (
              <g
                key={node.id}
                style={{ cursor: available ? 'pointer' : 'not-allowed' }}
                onClick={() => setSelectedId(node.id)}
              >
                <circle
                  cx={pos.x} cy={pos.y}
                  r={isSelected ? 22 : 18}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={isSelected ? 3 : 2}
                  opacity={available ? 1 : 0.4}
                />
                {lv > 0 && (
                  <text
                    x={pos.x} y={pos.y + 4}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="700"
                    fill={maxed ? '#ffd84a' : '#0a0d14'}
                  >
                    {lv}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Detail panel for selected node */}
        <div className="skillweb-detail">
          {selected ? <NodeDetail node={selected} onUpgrade={() => upgrade(selected.id)} /> : (
            <div className="skillweb-detail__empty">Knoten anklicken für Details</div>
          )}
        </div>
      </div>
    </div>
  );
}

function NodeDetail({ node, onUpgrade }: { node: SkillWebNode; onUpgrade: () => void }) {
  const player = useGameStore((s) => s.player)!;
  const t = useT();
  const lv = nodeLevel(player, node.id);
  const maxed = lv >= node.maxLevel;
  const available = isNodeAvailable(player, node);
  const cost = !maxed ? node.cost(lv) : {};
  const cleanCodeOk = (cost.cleanCode ?? 0) <= (player.cleanCode ?? 0);
  const resCost: Partial<Record<ResourceId, number>> = {};
  for (const k of ['nano_chip', 'data_track', 'hyper_processor_5'] as ResourceId[]) {
    const v = (cost as Record<string, number | undefined>)[k];
    if (v) resCost[k] = v;
  }
  const resOk = Object.keys(resCost).length === 0 || canAfford(player, resCost);
  const affordable = available && !maxed && cleanCodeOk && resOk;

  const nameKey = node.nameKey;
  const nodeTexts = (t.skillWeb.nodes as Record<string, string>);
  const name = nodeTexts[nameKey] ?? node.id;
  const desc = nodeTexts[`${nameKey}_desc`] ?? '';

  return (
    <div className={`skillweb-detail__card skillweb-detail__card--${node.ring}`}>
      <div className="skillweb-detail__ring">{node.ring}</div>
      <div className="skillweb-detail__name">{name}</div>
      <div className="skillweb-detail__desc">{desc}</div>
      <div className="skillweb-detail__level">
        Lv {lv} / {node.maxLevel}
      </div>

      {node.prerequisites.length > 0 && (
        <div className="skillweb-detail__prereq">
          <span>Voraussetzungen:</span>
          {node.prerequisites.map((req) => {
            const reqNode = getSkillWebNode(req.nodeId);
            const reqLv = nodeLevel(player, req.nodeId);
            const ok = reqLv >= req.minLevel;
            const reqName = (t.skillWeb.nodes as Record<string, string>)[reqNode?.nameKey ?? ''] ?? req.nodeId;
            return (
              <span key={req.nodeId} className={ok ? 'skillweb-detail__prereq-ok' : 'skillweb-detail__prereq-fail'}>
                {reqName} ≥ {req.minLevel}
              </span>
            );
          })}
        </div>
      )}

      {node.unlockVaultTier && (
        <div className="skillweb-detail__prereq">
          Vault-Tier ≥ {node.unlockVaultTier}{' '}
          <span className={(player.highestVaultTier ?? 0) >= node.unlockVaultTier
            ? 'skillweb-detail__prereq-ok' : 'skillweb-detail__prereq-fail'}>
            (du: {player.highestVaultTier ?? 0})
          </span>
        </div>
      )}

      {!maxed && (
        <>
          <div className="skillweb-detail__cost">
            <span>Kosten:</span>
            {(cost.cleanCode ?? 0) > 0 && (
              <span className={cleanCodeOk ? '' : 'skillweb-detail__cost-fail'}>
                {cost.cleanCode} ◈
              </span>
            )}
            {formatCost(cost as Partial<Record<ResourceId, number>>).filter((c) => c.key !== 'cleanCode').map((c) => (
              <span key={c.key}>{c.amount} {resourceLabel(c.key)}</span>
            ))}
          </div>
          <button
            className={`btn ${affordable ? 'btn--primary' : 'btn--ghost'}`}
            disabled={!affordable}
            onClick={onUpgrade}
          >
            {t.skillWeb.research}
          </button>
        </>
      )}
      {maxed && <div className="skillweb-detail__maxed">{t.skillWeb.maxed}</div>}
    </div>
  );
}
